import { ArrowsUpDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { SelectItem, SelectList } from 'app/components/SelectList';
import { EntityTypes } from 'constants/entities';
import { update } from 'core/actions/admin/data';
import { selectCourseVariables, selectVariablesInCourse } from 'core/selectors/admin/courseVariables';
import { selectOfflineId } from 'core/selectors/admin/offlineId';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ICourseVariableDAO } from 'types/domain/course';
import { IVariableDAO, VariableType } from 'types/domain/variable';
import TextEditor, { TextType } from 'app/components/TextEditor';
import { VariableValue } from 'app/pages/Admin/Modals/Variable/action';
import { VariableTypes } from 'constants/variables';
import { Modals, setModal } from 'core/slices/modals';
import { DndContext, KeyboardSensor, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { reduceArrayById } from 'utils/reduceArrayById';
import { CSS } from '@dnd-kit/utilities';
import { move } from 'core/actions/admin/diagram';

export const CourseVariables = memo(({ id, className }: { id?: number; className?: string }) => {
  const variables: IVariableDAO[] = useAppSelector(selectVariablesInCourse);
  const courseVariables: ICourseVariableDAO[] = useAppSelector(state => selectCourseVariables(state, id));
  const courseVariablesById = reduceArrayById(courseVariables);
  const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
  const offlineId = useAppSelector(selectOfflineId);
  const dispatch = useAppDispatch();
  const variableOptions = useMemo(() => {
    return variables
      .filter(v => !courseVariables.some(cv => cv.variableId === v.id))
      .map(variable => {
        return {
          name: variable.name,
          value: variable.id,
        } as SelectItem;
      });
  }, [courseVariables, variables]);

  const createCourseVariable = useCallback(
    variableId => {
      dispatch(
        update({
          type: EntityTypes.COURSE_VARIABLE,
          parentCourseId: courseId,
          entity: {
            id: offlineId,
            offlineId,
            courseId,
            variableId,
          },
        }),
      );
    },
    [courseId, dispatch, offlineId],
  );

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [items, setItems] = useState<number[]>([]);

  useEffect(() => {
    setItems(courseVariables.map((cn: any) => cn.id));
  }, [courseVariables]);

  const handleDragEnd = useCallback(
    event => {
      const { active, over } = event;

      if (active.id !== over.id) {
        setItems(items => {
          const oldIndex = items.indexOf(active.id);
          const newIndex = items.indexOf(over.id);
          const moved = arrayMove(items, oldIndex, newIndex);
          dispatch(
            move({
              parentEntityType: EntityTypes.COURSE,
              parentEntityId: courseId,
              items: moved.map((id, order) => {
                return {
                  type: EntityTypes.COURSE_VARIABLE,
                  id,
                  order,
                };
              }),
            }),
          );
          return moved;
        });
      }
    },
    [courseId, dispatch],
  );

  return (
    <div className="flex flex-col p-5">
      <table className={`${className}  text-gray-700 m-5`}>
        <thead>
          <tr className="text-black border-b border-gray-400  font-bold">
            <td></td>
            <td className="p-2">Name</td>
            <td className="p-2">Type</td>
            <td className="p-2">Pass</td>
            <td></td>
          </tr>
        </thead>
        <tbody>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={courseVariables}>
              {items
                .filter(item => courseVariablesById[item])
                .map(item => (
                  <CourseVariableRow key={item} courseVariable={courseVariablesById[item]} />
                ))}
            </SortableContext>
          </DndContext>
        </tbody>
      </table>
      <SelectList
        label={'Select record'}
        className="ml-auto mr-5"
        onChange={createCourseVariable}
        items={variableOptions}
      />
    </div>
  );
});

function CourseVariableRow({ courseVariable }: { courseVariable: ICourseVariableDAO }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: courseVariable.id });
  const courseId = useAppSelector(state => state.state[EntityTypes.COURSE]);
  const offlineId = useAppSelector(selectOfflineId);
  const dispatch = useAppDispatch();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  const onChangeCourseVariable = useCallback(val => {
    dispatch(
      update({
        type: EntityTypes.COURSE_VARIABLE,
        parentEntityType: EntityTypes.COURSE,
        parentEntityId: courseId,
        entity: val,
      }),
    );
  }, []);

  const onChangeVariable = useCallback(val => {
    dispatch(
      update({
        type: EntityTypes.VARIABLE,
        parentEntityType: EntityTypes.COURSE,
        parentEntityId: courseId,
        entity: val,
      }),
    );
  }, []);

  const onSelect = useCallback(
    variableId => {
      dispatch(
        update({
          type: EntityTypes.COURSE_VARIABLE,
          parentEntityType: EntityTypes.COURSE,
          parentEntityId: courseId,
          entity: { id: offlineId, offlineId, courseId, variableId, status: 0 },
        }),
      );
    },
    [courseId, dispatch, offlineId],
  );

  return (
    <tr ref={setNodeRef} style={style} key={courseVariable.id}>
      <td>
        <ArrowsUpDownIcon {...listeners} {...attributes} className={`hidden md:block ml-2 mr-auto`} />
      </td>
      <td>
        <TextEditor
          type={TextType.SHORT_TEXT}
          textId={courseVariable.variable.textId}
          parentType={EntityTypes.VARIABLE}
          parentId={courseVariable.variableId}
          className="w-full"
        />
      </td>
      <td>
        <SelectList
          items={VariableTypes}
          value={courseVariable.variable.type || VariableType.BOOLEAN}
          label={'Record type'}
          className="w-44 mr-5 text-gray-800 dark:text-white"
          onChange={type => onChangeVariable({ id: courseVariable.id, type })}
        />
      </td>
      <td>
        <VariableValue
          onChange={value =>
            onChangeCourseVariable({
              id: courseVariable.id,
              ...value,
            })
          }
          value={courseVariable.value}
          variableOptionId={courseVariable.variableOptionId}
          variable={courseVariable.variable}
        />
      </td>

      <td>
        <TrashIcon
          className="w-5 h-5 cursor-pointer hover:text-black"
          onClick={e =>
            dispatch(
              setModal({
                type: Modals.DELETE,
                entityType: EntityTypes.COURSE_VARIABLE,
                id: courseVariable.id,
              }),
            )
          }
        />
      </td>
    </tr>
  );
}

export function CourseVariableTotal({ courseVariable }: { courseVariable: ICourseVariableDAO }) {
  const value = useMemo(() => {
    switch (courseVariable.variable.type) {
      case VariableType.SCORE:
        return courseVariable.variableCount ? (
          <div className="ml-5">
            <span className="text-sm text-gray-700 ">{`Total: ${courseVariable.variableCount}`}</span>
            {courseVariable.variableTotalScore ? (
              <span className="text-sm text-gray-700 ">{`Avg: ${(
                courseVariable.variableTotalScore / courseVariable.variableCount
              ).toFixed(2)}`}</span>
            ) : null}
          </div>
        ) : null;
    }
  }, [courseVariable]);

  return (
    <div className="flex flex-col justify-start items-start w-full">
      <div className="flex p-2 w-full items-center">
        <span className="text-md">{courseVariable.variable?.name}</span>
      </div>
      {value}
    </div>
  );
}
